import {
  Button,
  Checkbox,
  HSpacer,
  MenuItem,
  NumericInput,
  SegmentedButton,
  Select,
  Text,
  VSpacer,
} from "@/components/DesignSystem";
import Check from "@mui/icons-material/Check";
import { Stack } from "@mui/material";
import { RewardsType } from "@shared/enums/RewardsType";
import {
  formatRewardsUoM,
  RewardsUom,
  RewardsUomAcceptedSpellings,
} from "@shared/enums/RewardsUom";
import { SharedConfig } from "@shared/SharedConfig";
import { round } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const { rewardsMaxRewardPercent } = SharedConfig.settings;

interface RewardsLoyaltyValueInputProps {
  categoryName?: string,
  disableUomSelector?: boolean,
  disabled?: boolean,
  hideOnDashboard?: boolean,
  innerContainerWidth?: string,
  onClickHideOnDashboard?: (value: boolean) => void,
  onUpdate: (updates: {
    hideOnDashboard?: boolean,
    rewardType?: RewardsType,
    uom?: RewardsUom,
    value?: number,
  }) => void,
  maxPercentValue?: number | null,
  rewardType?: RewardsType,
  testID: string,
  uom?: RewardsUom,
  value?: number,
  width?: string,
}

export const RewardsLoyaltyValueInput = ({
  categoryName,
  disableUomSelector,
  disabled,
  hideOnDashboard,
  innerContainerWidth = '100%',
  maxPercentValue,
  onClickHideOnDashboard,
  onUpdate,
  rewardType,
  testID,
  uom,
  value,
  width = '643px',
}: RewardsLoyaltyValueInputProps) => {
  const { t } = useTranslation();
  const normalizedMaxPercentValue = maxPercentValue === undefined
    ? rewardsMaxRewardPercent * 100
    : maxPercentValue;

  const [isFixed, setIsFixed] = useState(uom === RewardsUom.Dollars);

  const UOMs = Object.keys(RewardsUomAcceptedSpellings).filter(
    uom => uom !== RewardsUom.Dollars) as RewardsUom[];

  return (
    <Stack width={width}>
      {!!categoryName && (
        <>
          <Text category="title-medium">
            {categoryName}
          </Text>
          <VSpacer size="5" />
        </>
      )}
      <Stack alignSelf="center" width={innerContainerWidth}>
        <Text category="title-small">
          {t($ => $.shared.loyaltyValue.pointsEarnedBased)}
        </Text>
        <VSpacer size="2"/>
        <SegmentedButton
          color="primary"
          disabled={disabled}
          fullWidth
          selectedIndex={isFixed ? 0 : 1}
        >
          <Button
            aria-selected={isFixed}
            key="Fixed"
            onClick={() => {
              onUpdate({
                uom: RewardsUom.Dollars,
                rewardType: RewardsType.Dollars,
                value: undefined,
              });
              setIsFixed(true);
            }}
            startIcon={isFixed && <Check sx={{ width: "17px", height: "17px" }} />}
            testID={`${testID}-fixed-button`}
          >
            {t($ => $.shared.loyaltyValue.dollarAmountSpent)}
          </Button>
          <Button
            aria-selected={!isFixed}
            key="Proportional"
            onClick={() => {
              onUpdate({
                hideOnDashboard,
                rewardType: RewardsType.Dollars,
                uom: undefined,
                value: undefined,
              });
              setIsFixed(false);
            }}
            startIcon={!isFixed && <Check sx={{ width: "17px", height: "17px" }} />}
            testID={`${testID}-proportional-button`}
          >
            {t($ => $.shared.loyaltyValue.qtyPerUomPurchased)}
          </Button>
        </SegmentedButton>
        <VSpacer size="10" />
        <Stack alignItems="center" p="20px 24px 32px 24px">
          {isFixed && (
            <Stack>
              <Text category="body-large">
                {t($ => $.shared.loyaltyValue.forEveryDollarSpent)}
              </Text>
              <VSpacer size="5" />
              <Stack alignItems="center" alignSelf="center" direction="row">
                <Select
                  disabled={disabled}
                  onChangeValue={(rewardsType) => (
                    onUpdate({
                      hideOnDashboard,
                      rewardType: rewardsType as RewardsType,
                      uom,
                      value: rewardsType === RewardsType.Dollars
                        ? value && round(value / 100, 4)
                        : value && round(
                          normalizedMaxPercentValue === null
                            ? value * 100
                            : Math.min(value * 100, normalizedMaxPercentValue),
                          5,
                        ),
                    })
                  )}
                  testID={`${testID}-rewards-type`}
                  value={rewardType}
                  width={68}
                >
                  <MenuItem key="%" testID={`${testID}-rewards-type-percent}`} value="%">
                    %
                  </MenuItem>
                  <MenuItem key="$" testID={`${testID}-rewards-type-dollars}`} value="$">
                    $
                  </MenuItem>
                </Select>
                <HSpacer size="3" />
                <NumericInput
                  decimals={rewardType === RewardsType.Dollars ? 4 : 3}
                  disabled={disabled}
                  maxValue={rewardType === RewardsType.Percent
                    ? normalizedMaxPercentValue ?? undefined
                    : undefined}
                  minValue={0}
                  onChangeNumber={(value) => onUpdate({
                    hideOnDashboard,
                    rewardType,
                    uom,
                    value: rewardType === RewardsType.Percent
                      ? value && (
                        normalizedMaxPercentValue === null
                          ? value
                          : Math.min(value, normalizedMaxPercentValue)
                      )
                      : value,
                  })}
                  placeholder={rewardType === RewardsType.Dollars ? "0.0000" : "0.000"}
                  testID={`${testID}-fixed-rewards-value`}
                  value={value}
                  width="112px"
                />
                <HSpacer size="4" />
                <Text category="body-large">
                  {t($ => $.shared.loyaltyValue.inPoints)}
                </Text>
              </Stack>
            </Stack>
          )}
          {!isFixed && (
            <Stack alignItems="center">
              <VSpacer size="2" />
              <Stack alignItems="center" alignSelf="center" direction="row">
                <Text category="body-large">
                  {t($ => $.shared.loyaltyValue.forEveryUnit)}
                </Text>
                <HSpacer size="3" />
                <Select
                  disabled={disabled || disableUomSelector}
                  label={t($ => $.shared.loyaltyValue.uom)}
                  onChangeValue={(uom) => (
                    onUpdate({ hideOnDashboard, rewardType, uom: uom as RewardsUom, value })
                  )}
                  testID={`${testID}-rewards-type`}
                  value={uom}
                  width={140}
                >
                  {UOMs.map(uom => (
                    <MenuItem key={uom} testID={`${testID}-rewards-type-${uom}`} value={uom}>
                      {formatRewardsUoM(1, uom)}
                    </MenuItem>
                  ))}
                </Select>
                <HSpacer size="3" />
                <Text category="body-large">
                  {t($ => $.shared.loyaltyValue.purchased)}
                </Text>
              </Stack>
              <VSpacer size="6" />
              <Stack alignItems="center" alignSelf="center" direction="row">
                <Text category="body-large">
                  {t($ => $.shared.loyaltyValue.customerWillReceive)}
                </Text>
                <HSpacer size="3" />
                <NumericInput
                  decimals={4}
                  disabled={disabled}
                  minValue={0}
                  onChangeNumber={(value) => onUpdate({ hideOnDashboard, rewardType, uom, value })}
                  placeholder="$0.0000"
                  prefix='$'
                  testID={`${testID}-proportional-rewards-value`}
                  value={value}
                  width="112px"
                />
                <HSpacer size="3" />
                <Text category="body-large">
                  {t($ => $.shared.loyaltyValue.inPoints)}
                </Text>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Stack>
      {!!onClickHideOnDashboard && (
        <>
          <VSpacer size="7" />
          <Checkbox
            checked={hideOnDashboard}
            onChangeChecked={onClickHideOnDashboard}
            testID="hide-on-customer-dashboard-checkbox"
          >
            <Text category="body-large">{t($ => $.shared.loyaltyValue.hideOnDashboard)}</Text>
          </Checkbox>
        </>
      )}
    </Stack>
  );
};
